import type { ValueOf } from '#layers/base/public';
import type { OnOptionsChangeHelpers } from '#layers/data-model/types/data-sources/define';
import { isNullishOrEmpty } from '#layers/base/public';

const connectionStringField = 'connectionString';

/**
 * Issues surfaced while parsing a connection string. Severity and message are
 * fixed per issue; a connector controls whether an issue can occur through its
 * parse config (e.g. `sslUnsupportedModes`), not by remapping the message.
 */
export const ConnectionStringIssue = {
	UnescapedSpecialChars: 'unescaped_special_chars',
	UnsupportedSslMode: 'unsupported_ssl_mode',
} as const;
export type ConnectionStringIssue = ValueOf<typeof ConnectionStringIssue>;

const CONNECTION_STRING_ISSUE_MESSAGES: Record<ConnectionStringIssue, { severity: 'error' | 'warning'; message: string }> = {
	unescaped_special_chars: {
		severity: 'warning',
		message: '$t:connectors.common.connection_string_unescaped_chars',
	},
	unsupported_ssl_mode: {
		severity: 'error',
		message: '$t:connectors.common.unsupported_ssl_mode',
	},
};

export interface ParseConnectionStringOptions {
	connectionString: string;
	protocols: readonly string[];
	sslKeys: readonly string[];
	sslAllowedModes: readonly string[];
	/**
	 * Canonical ssl-mode values that are recognized but not supported by this
	 * connector. A pasted value resolving to one of these reports
	 * `UnsupportedSslMode` instead of being silently dropped.
	 */
	sslUnsupportedModes?: readonly string[];
	sslModeAliases?: Record<string, string>;
}

export interface ParsedConnectionString {
	options: {
		host?: string;
		port?: number;
		user?: string;
		password?: string;
		dbname?: string;
		ssl?: { mode: string };
	};
	issues: ConnectionStringIssue[];
}

interface ConnectionStringParseResult<TOptions extends Record<string, any>> {
	options: Partial<TOptions>;
	issues: ConnectionStringIssue[];
}

interface ConnectionStringOptionsChangeConfig<TOptions extends Record<string, any>> {
	parse: (connectionString: string) => ConnectionStringParseResult<TOptions>;
	invalidMessage: string;
	fields: readonly (keyof TOptions)[];
	requiredFields?: readonly (keyof TOptions)[];
}

function lenientDecode(input: string): { value: string; hadInvalidEscape: boolean } {
	try {
		return { value: decodeURIComponent(input), hadInvalidEscape: false };
	}
	catch {
		return { value: input, hadInvalidEscape: true };
	}
}

export function parseConnectionString(config: ParseConnectionStringOptions): ParsedConnectionString {
	let url: URL;

	try {
		url = new URL(config.connectionString);
	}
	catch {
		return { options: {}, issues: [] };
	}

	if (!config.protocols.includes(url.protocol)) {
		return { options: {}, issues: [] };
	}

	const issues: ConnectionStringIssue[] = [];

	const user = lenientDecode(url.username);
	const password = lenientDecode(url.password);
	const dbname = lenientDecode(url.pathname.slice(1));

	const options: ParsedConnectionString['options'] = {};

	if (url.hostname) options.host = url.hostname;
	if (url.port) options.port = Number.parseInt(url.port, 10);
	if (user.value) options.user = user.value;
	if (password.value) options.password = password.value;
	if (dbname.value) options.dbname = dbname.value;

	if (user.hadInvalidEscape || password.hadInvalidEscape || dbname.hadInvalidEscape) {
		issues.push(ConnectionStringIssue.UnescapedSpecialChars);
	}

	const resolvedSslMode = config.sslKeys
		.map((key) => url.searchParams.get(key))
		.map((value) => value?.toLowerCase())
		.map((value) => value ? (config.sslModeAliases?.[value] ?? value) : value)
		.find((value): value is string => !!value
			&& (config.sslAllowedModes.includes(value) || (config.sslUnsupportedModes?.includes(value) ?? false)));

	if (resolvedSslMode) {
		if (config.sslAllowedModes.includes(resolvedSslMode)) options.ssl = { mode: resolvedSslMode };
		else issues.push(ConnectionStringIssue.UnsupportedSslMode);
	}

	return { options, issues };
}

interface ConnectionStringFeedback {
	error?: string | null;
	warning?: string | null;
}

/**
 * Write both the error and warning slots of the connection-string field in one
 * call. Error and warning are independent channels, so each branch must declare
 * the full state; omitted severities default to `null` (cleared) so a stale
 * message from the opposite slot can't survive.
 */
export function setConnectionStringFeedback(
	helpers: Pick<OnOptionsChangeHelpers<{ connectionString?: string }>, 'setFieldError' | 'setFieldWarning'>,
	{ error = null, warning = null }: ConnectionStringFeedback = {},
): void {
	helpers.setFieldError(connectionStringField, error);
	helpers.setFieldWarning(connectionStringField, warning);
}

export function createConnectionStringOptionsChangeHandler<
	TOptions extends { connectionString?: string } & Record<string, any>,
>(
	config: ConnectionStringOptionsChangeConfig<TOptions>,
) {
	return (
		newVal: TOptions,
		_oldVal: TOptions,
		{ getDefaultOptions, setValues, setFieldError, setFieldWarning, hasChanged, getFieldLabel }: OnOptionsChangeHelpers<TOptions>,
	) => {
		const feedback = { setFieldError, setFieldWarning };

		if (!hasChanged(connectionStringField)) {
			return;
		}

		const connectionString = newVal[connectionStringField];

		if (isNullishOrEmpty(connectionString)) {
			setConnectionStringFeedback(feedback);
			return;
		}

		const { options: parsed, issues } = config.parse(connectionString);

		// Unparseable or wrong protocol -> generic message.
		if (Object.keys(parsed).length === 0) {
			setConnectionStringFeedback(feedback, { error: config.invalidMessage });
			return;
		}

		// Parsed, but required fields are absent -> name them.
		const missingFields = config.requiredFields?.filter((field) => parsed[field] === undefined) ?? [];

		if (missingFields.length > 0) {
			const fields = missingFields
				.map((field) => getFieldLabel(field) ?? String(field))
				.join(', ');

			setConnectionStringFeedback(feedback, {
				error: `$t:connectors.common.connection_string_missing(${JSON.stringify({ fields })})`,
			});

			return;
		}

		const updates: Partial<TOptions> = {};
		const defaultOptions = getDefaultOptions();

		// A pasted string is the authority for every field it owns, so a field it
		// omits falls back to the connector default and otherwise clears. Skipping
		// the absent ones instead would strand the previous string's value: a
		// password-less string replacing one that carried a password would keep the
		// old password and submit it against the new host and user.
		for (const field of config.fields) {
			updates[field] = parsed[field] ?? defaultOptions[field];
		}

		setValues(updates);

		// A blocking error (e.g. an unsupported ssl-mode) coexists with the applied
		// field updates: we still autofill host/port/etc. but surface the problem.
		const messageWith = (severity: 'error' | 'warning') =>
			issues
				.map((issue) => CONNECTION_STRING_ISSUE_MESSAGES[issue])
				.find((descriptor) => descriptor.severity === severity)
				?.message ?? null;

		setConnectionStringFeedback(feedback, {
			error: messageWith('error'),
			warning: messageWith('warning'),
		});
	};
}
